import { WORKLET_SOURCE } from "./worklet-source.js";
import type { LiveEvent, LiveOptions, LiveSession, LiveStrategy } from "./types.js";

const DEFAULT_BASE_URL = "https://transcribe.unveiledsoftwaresolutions.com";

/**
 * Start a live transcription session.
 *
 * Captures mic audio in an AudioWorklet, streams it as PCM s16le over a
 * WebSocket to the cluster's `/v1/live`, and surfaces server-emitted
 * events through callbacks on `opts`. Returns once the WS handshake and
 * mic permission have both succeeded; subsequent decoder events arrive
 * via the provided callbacks until `session.stop()` or `session.cancel()`
 * is called.
 *
 * By default the lib requests the `local_agreement` strategy (the
 * streaming-with-revision decoder). Pass `strategy: "fixed_chunk"` to
 * fall back to the legacy non-revising behavior.
 *
 * **Token security:** browsers can't set Authorization headers on a WS
 * connect, so the lib forwards the bearer token as `?token=<...>`. Don't
 * ship the token in a client-side bundle — read it from a Tauri command
 * at runtime, or use a WS-proxy endpoint via `opts.endpoint`.
 */
export async function live(opts: LiveOptions): Promise<LiveSession> {
  if (typeof window === "undefined" || typeof AudioContext === "undefined") {
    throw new Error(
      "@unveiled/voice-engine: live() requires a browser environment with AudioContext + WebSocket.",
    );
  }

  const strategy: LiveStrategy = opts.strategy ?? "local_agreement";
  const baseUrl = (opts.baseUrl ?? DEFAULT_BASE_URL).replace(/\/+$/, "");
  const wsUrl = buildWsUrl(baseUrl, strategy, opts);

  // --- WebSocket ---------------------------------------------------------
  const ws = new WebSocket(wsUrl);
  ws.binaryType = "arraybuffer";

  // State surfaced to the caller via accessors.
  let committedText = "";
  let provisionalText = "";
  let levelRMS = 0;
  let closed = false;
  let finalText: string | null = null;
  let finalResolver: ((text: string) => void) | null = null;

  ws.onmessage = (ev) => {
    if (typeof ev.data !== "string") return;
    let raw: Record<string, unknown>;
    try {
      raw = JSON.parse(ev.data);
    } catch {
      opts.onError?.("bad server frame: not JSON");
      return;
    }
    const event = parseEvent(raw);
    if (!event) return;
    opts.onEvent?.(event);

    switch (event.type) {
      case "ready":
        opts.onReady?.({
          model: event.model,
          sampleRate: event.sampleRate,
          strategy: event.strategy,
        });
        break;
      case "partial":
        opts.onPartial?.({
          text: event.text,
          chunkIndex: event.chunkIndex,
          tStartMs: event.tStartMs,
          tEndMs: event.tEndMs,
        });
        break;
      case "committed":
        committedText = appendCommitted(committedText, event.text);
        // Trim the leading edge of provisional that's now been committed.
        // The decoder will resend the up-to-date provisional on its next
        // tick, so the simplest safe move is to clear it here.
        provisionalText = "";
        opts.onCommitted?.({
          text: event.text,
          tStartMs: event.tStartMs,
          tEndMs: event.tEndMs,
        });
        break;
      case "provisional":
        provisionalText = event.text;
        opts.onProvisional?.({
          text: event.text,
          tStartMs: event.tStartMs,
          tEndMs: event.tEndMs,
        });
        break;
      case "final":
        committedText = appendCommitted(committedText, event.text);
        provisionalText = "";
        finalText = committedText;
        opts.onFinal?.({
          text: event.text,
          tStartMs: event.tStartMs,
          tEndMs: event.tEndMs,
        });
        if (finalResolver) finalResolver(committedText);
        break;
      case "error":
        opts.onError?.(event.message);
        break;
      case "close":
        // synthesized below; nothing to do here
        break;
    }
  };
  ws.onclose = () => {
    closed = true;
    opts.onEvent?.({ type: "close" });
    opts.onClose?.();
    // If stop() is waiting for a final and the server hung up before
    // sending one, resolve with whatever we've got so the caller isn't
    // stuck forever.
    if (finalResolver) finalResolver(finalText ?? committedText);
  };

  // Wait for connection open. Browsers' WebSocket fires `error` then
  // `close` for handshake failures, so we surface either.
  await new Promise<void>((resolve, reject) => {
    ws.onopen = () => resolve();
    const failHandler = () => reject(new Error(`live ws handshake failed: ${wsUrl}`));
    ws.addEventListener("error", failHandler, { once: true });
    ws.addEventListener("close", failHandler, { once: true });
  });

  // --- Mic capture -------------------------------------------------------
  const externalStream = opts.stream !== undefined;
  const stream =
    opts.stream ??
    (await navigator.mediaDevices.getUserMedia({ audio: true }).catch((err) => {
      ws.close();
      throw err;
    }));
  const ctx = new AudioContext();
  await ctx.audioWorklet.addModule(getWorkletUrl()).catch((err) => {
    ws.close();
    throw err;
  });
  const src = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "voice-engine-pcm-capture");
  const srcRate = ctx.sampleRate;

  node.port.onmessage = (ev) => {
    if (closed || ws.readyState !== WebSocket.OPEN) return;
    const f32 = ev.data as Float32Array;
    const ds = downsampleTo16k(f32, srcRate);
    // Update level meter.
    let s = 0;
    for (let i = 0; i < ds.length; i++) {
      const v = ds[i] ?? 0;
      s += v * v;
    }
    levelRMS = Math.min(1, Math.sqrt(s / Math.max(1, ds.length)));
    // f32 -> s16le binary frame.
    const s16 = floatToS16le(ds);
    try {
      ws.send(s16.buffer);
    } catch {
      // best effort — if the WS chokes the close handler will take over
    }
  };
  src.connect(node);
  // Don't connect to ctx.destination — no playback.

  // --- Lifecycle ---------------------------------------------------------
  let stopped = false;
  const teardownMic = async () => {
    try {
      src.disconnect();
      node.disconnect();
    } catch {}
    if (!externalStream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    try {
      await ctx.close();
    } catch {}
  };

  const stop = async (): Promise<string> => {
    if (stopped) return finalText ?? committedText;
    stopped = true;
    // Tell the server to flush its tail. Then wait for the `final` event
    // (or for the WS to close, whichever first).
    const wait = new Promise<string>((resolve) => {
      finalResolver = resolve;
      // Guard against the server never replying.
      setTimeout(() => resolve(finalText ?? committedText), 10_000);
    });
    if (ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "stop" }));
      } catch {}
    }
    const text = await wait;
    // LocalAgreement-2 keeps the last-spoken words in `provisionalText` until
    // they stabilise. A `final` event folds them into committed (and clears
    // provisional), but if the server closed or we hit the timeout before any
    // `final` arrived, `text` is just the committed prefix — so the still-
    // provisional tail (the end of what the user said) would be silently
    // dropped. Merge it back in so stop() never truncates the last words.
    const tail = provisionalText.trim();
    const merged = tail && !text.trim().endsWith(tail) ? appendCommitted(text, tail) : text;
    await teardownMic();
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
    return merged;
  };

  const cancel = async (): Promise<void> => {
    if (stopped) return;
    stopped = true;
    await teardownMic();
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close();
    }
  };

  return {
    stop,
    cancel,
    level: () => levelRMS,
    committedText: () => committedText,
    provisionalText: () => provisionalText,
  };
}

// ---- helpers -----------------------------------------------------------

function buildWsUrl(
  baseUrl: string,
  strategy: LiveStrategy,
  opts: LiveOptions,
): string {
  if (opts.endpoint) {
    // Treat the proxy endpoint as already a ws/wss URL or convertible from http.
    const u = httpToWs(opts.endpoint);
    const appended = appendParams(u, {
      strategy,
      model: opts.model,
      language: opts.language,
      profile: opts.profile,
      initial_prompt: opts.initialPrompt,
      chunk_ms: opts.chunkMs?.toString(),
      overlap_ms: opts.overlapMs?.toString(),
    });
    return appended;
  }
  if (!opts.token) {
    throw new Error(
      "@unveiled/voice-engine: live() requires either `endpoint` (WS proxy) or `token` (direct connect).",
    );
  }
  const wsBase = httpToWs(baseUrl);
  return appendParams(`${wsBase}/v1/live`, {
    strategy,
    token: opts.token,
    model: opts.model,
    language: opts.language,
    profile: opts.profile,
    initial_prompt: opts.initialPrompt,
    chunk_ms: opts.chunkMs?.toString(),
    overlap_ms: opts.overlapMs?.toString(),
  });
}

function httpToWs(url: string): string {
  if (url.startsWith("https://")) return "wss://" + url.slice("https://".length);
  if (url.startsWith("http://")) return "ws://" + url.slice("http://".length);
  return url; // assume already ws/wss
}

function appendParams(url: string, params: Record<string, string | undefined>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  }
  if (parts.length === 0) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}${parts.join("&")}`;
}

function parseEvent(raw: Record<string, unknown>): LiveEvent | null {
  const type = raw["type"];
  if (typeof type !== "string") return null;
  switch (type) {
    case "ready":
      return {
        type: "ready",
        model: String(raw["model"] ?? ""),
        sampleRate: Number(raw["sample_rate"] ?? 16000),
        strategy: (String(raw["strategy"] ?? "fixed_chunk") as LiveStrategy),
      };
    case "partial":
      return {
        type: "partial",
        text: String(raw["text"] ?? ""),
        chunkIndex: Number(raw["chunk_index"] ?? 0),
        tStartMs: Number(raw["t_start_ms"] ?? 0),
        tEndMs: Number(raw["t_end_ms"] ?? 0),
      };
    case "committed":
      return {
        type: "committed",
        text: String(raw["text"] ?? ""),
        tStartMs: Number(raw["t_start_ms"] ?? 0),
        tEndMs: Number(raw["t_end_ms"] ?? 0),
      };
    case "provisional":
      return {
        type: "provisional",
        text: String(raw["text"] ?? ""),
        tStartMs: Number(raw["t_start_ms"] ?? 0),
        tEndMs: Number(raw["t_end_ms"] ?? 0),
      };
    case "final":
      return {
        type: "final",
        text: String(raw["text"] ?? ""),
        tStartMs: Number(raw["t_start_ms"] ?? 0),
        tEndMs: Number(raw["t_end_ms"] ?? 0),
      };
    case "error":
      return {
        type: "error",
        message: String(raw["message"] ?? "unknown error"),
      };
    default:
      return null;
  }
}

/** Smart join — separate with a single space unless the new chunk starts
 * with punctuation, in which case append directly. */
function appendCommitted(prev: string, next: string): string {
  const trimmed = next.trim();
  if (!trimmed) return prev;
  if (!prev) return trimmed;
  const startsWithPunct = /^[.,;:!?\)\]]/.test(trimmed);
  return startsWithPunct ? `${prev}${trimmed}` : `${prev} ${trimmed}`;
}

function downsampleTo16k(input: Float32Array, srcRate: number): Float32Array {
  if (srcRate === 16000) return input;
  if (srcRate < 16000) return input;
  const ratio = srcRate / 16000;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.floor((i + 1) * ratio);
    let sum = 0;
    let n = 0;
    for (let j = start; j < end && j < input.length; j++) {
      sum += input[j] ?? 0;
      n++;
    }
    out[i] = n > 0 ? sum / n : 0;
  }
  return out;
}

function floatToS16le(f: Float32Array): Int16Array {
  const out = new Int16Array(f.length);
  for (let i = 0; i < f.length; i++) {
    const s = Math.max(-1, Math.min(1, f[i] ?? 0));
    out[i] = Math.round(s < 0 ? s * 0x8000 : s * 0x7fff);
  }
  return out;
}

let workletUrl: string | null = null;
function getWorkletUrl(): string {
  if (!workletUrl) {
    const blob = new Blob([WORKLET_SOURCE], {
      type: "application/javascript",
    });
    workletUrl = URL.createObjectURL(blob);
  }
  return workletUrl;
}
